import { useEffect, useRef, useState } from "react";
import { IoAdd } from "react-icons/io5";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";

import { handleError } from "src/Common/function";
import {
  Dropzone,
  GhostButton,
  ModalShell,
  PrimaryButton,
  TrashIcon,
  compressIfNeeded,
  formatBytes,
  isAcceptedFile,
} from "../upload/uploadModalUI";
import {
  DocumentReviewModal,
  DocumentScanningModal,
  getDocumentDisplayName,
} from "./DocumentScanModals";

let itemIdCounter = 0;
const nextItemId = () => `doc-file-${Date.now()}-${itemIdCounter++}`;

const isImageFile = (file) =>
  file?.type?.startsWith("image/") || /\.(png|jpe?g|gif|bmp|webp|svg|heic|heif|tiff?)$/i.test(file?.name || "");

const buildItem = (file) => ({
  id: nextItemId(),
  file,
  previewUrl: isImageFile(file) ? URL.createObjectURL(file) : null,
});

// Blue file-pdf glyph on a #DBEAFE tile, matching the staged-file card design.
const PdfFileIcon = () => (
  <div className="w-[48px] h-[48px] shrink-0 rounded-[8px] bg-[#DBEAFE] flex items-center justify-center">
    <svg width="21" height="28" viewBox="0 0 384 512" fill="#0065FD">
      <path d="M181.9 256.1c-5-16-4.9-46.9-2-46.9 8.4 0 7.6 36.9 2 46.9zm-1.7 47.2c-7.7 20.2-17.3 43.3-28.4 62.7 18.3-7 39-17.2 62.9-21.9-12.7-9.6-24.9-23.4-34.5-40.8zM86.1 428.1c0 .8 13.2-5.4 34.9-40.2-6.7 6.3-29.1 24.5-34.9 40.2zM248 160h136v328c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V24C0 10.7 10.7 0 24 0h200v136c0 13.2 10.8 24 24 24zm-8 171.8c-20-12.2-33.3-29-42.7-53.8 4.5-18.5 11.6-46.6 6.2-64.2-4.7-29.4-42.4-26.5-47.8-6.8-5 18.3-.4 44.1 8.1 77-11.6 27.6-28.7 64.6-40.8 85.8-.1 0-.1.1-.2.1-27.1 13.9-73.6 44.5-54.5 68 5.6 6.9 16 10 21.5 10 17.9 0 35.7-18 61.1-61.8 25.8-8.5 54.1-19.1 79-23.2 21.7 11.8 47.1 19.5 64 19.5 29.2 0 31.2-32 19.7-43.4-13.9-13.6-54.3-9.7-73.6-7.2zM377 105L279 7c-4.5-4.5-10.6-7-17-7h-6v128h128v-6.1c0-6.3-2.5-12.4-7-16.9zm-74.1 255.3c4.1-2.7-2.5-11.9-42.8-9 37.1 15.8 42.8 9 42.8 9z" />
    </svg>
  </div>
);

/**
 * Document upload with total extraction ("Upload And Scan"), shared by WO
 * proposals, WO invoices, and PO receipts.
 *
 * Phases: stage (dropzone / staged list) -> per new document: scanning
 * (backend scan endpoint) -> review (confirm / pick / manual). Multiple files
 * are reviewed sequentially. Closing a review skips that file (it stays Needs
 * Review); cancelling the scan aborts the remaining queue. Every exit path
 * refreshes the parent so new rows always appear.
 *
 * The caller injects the transport, since the three flows post to different
 * endpoints with different shapes:
 *   uploadFn(files, { documentName }) -> Promise<newDocuments[]>
 *   scanFn(documentId)                -> Promise<{ status, best, candidates, document }>
 *   confirmFn(documentId, amount, sourcePage, sourceLabel) -> Promise
 */
const DocumentUploadScanModal = ({
  shown,
  noun,
  totalLabel,
  accept,
  footer,
  multiple = true,
  // PO receipts have no documentName column, so their flow hides the field.
  allowRename = true,
  getFileUrl,
  uploadFn,
  scanFn,
  confirmFn,
  onClose,
  onUploaded,
}) => {
  const [items, setItems] = useState([]);
  const [documentName, setDocumentName] = useState("");
  const [documentNameTouched, setDocumentNameTouched] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState("stage"); // stage | scanning | review
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null); // { document, result }
  const [busy, setBusy] = useState(false);
  const [totalToReview, setTotalToReview] = useState(0);

  // Bumped when the flow is cancelled so late scan responses are ignored.
  const flowIdRef = useRef(0);
  // Mirror of `items` so cleanup can revoke the latest preview URLs.
  const itemsRef = useRef([]);
  itemsRef.current = items;

  // Reset whenever the modal (re)opens.
  useEffect(() => {
    if (!shown) return;
    itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
    flowIdRef.current++;
    setItems([]);
    setDocumentName("");
    setDocumentNameTouched(false);
    setUploading(false);
    setPhase("stage");
    setQueue([]);
    setCurrent(null);
    setBusy(false);
    setTotalToReview(0);
  }, [shown]);

  useEffect(
    () => () => itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl)),
    [],
  );

  if (!shown) return null;

  const addFiles = (files) => {
    const accepted = files.filter((file) => isAcceptedFile(file, accept));
    if (accepted.length < files.length) toast(`Unsupported file type. ${footer}`);
    if (!accepted.length) return;
    // Single-file flows (PO receipts) keep only the newest selection.
    const staged = multiple ? accepted : accepted.slice(-1);
    setItems((prev) => {
      const kept = multiple ? prev : [];
      if (!multiple) prev.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
      const next = [...kept, ...staged.map(buildItem)];
      // Prefill Document Name from the file name while there is one file and
      // the user hasn't typed their own value.
      if (next.length === 1 && !documentNameTouched) setDocumentName(next[0].file.name);
      return next;
    });
  };

  const removeItem = (id) =>
    setItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((item) => item.id !== id);
    });

  const finishFlow = () => {
    flowIdRef.current++;
    onUploaded?.();
    onClose?.();
  };

  const startScan = async (document, remaining) => {
    const flowId = flowIdRef.current;
    setQueue(remaining);
    setCurrent({ document, result: null });
    setPhase("scanning");
    try {
      const res = await scanFn(document.id);
      if (flowIdRef.current !== flowId) return; // flow was cancelled
      setCurrent({ document: res.data?.document || document, result: res.data });
      setPhase("review");
    } catch (error) {
      if (flowIdRef.current !== flowId) return;
      // Scan failure = nothing extracted; offer manual entry / retry.
      setCurrent({ document, result: { status: "NOT_FOUND", best: null, candidates: [] } });
      setPhase("review");
    }
  };

  const advance = () => {
    if (queue.length) {
      const [next, ...rest] = queue;
      startScan(next, rest);
    } else {
      finishFlow();
    }
  };

  const handleUploadAndScan = async () => {
    if (!items.length) return;
    setUploading(true);
    try {
      const files = [];
      for (const item of items) {
        files.push(await compressIfNeeded(item.file, imageCompression));
      }
      const newDocuments = await uploadFn(files, {
        documentName: allowRename && items.length === 1 ? documentName.trim() : "",
      });
      if (!newDocuments) return; // uploadFn already reported the failure

      toast(`${files.length} ${noun.toLowerCase()}${files.length > 1 ? "s" : ""} uploaded successfully`);
      if (!newDocuments.length) {
        finishFlow();
        return;
      }
      setTotalToReview(newDocuments.length);
      const [first, ...rest] = newDocuments;
      startScan(first, rest);
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async (amount, sourcePage, sourceLabel) => {
    if (!current?.document) return;
    setBusy(true);
    try {
      await confirmFn(current.document.id, amount, sourcePage, sourceLabel);
      toast(`${noun} amount confirmed`);
      advance();
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  // ---- scanning / review phases ----
  if (phase === "scanning" && current) {
    const index = totalToReview - queue.length;
    const label =
      totalToReview > 1
        ? `${getDocumentDisplayName(current.document)} (file ${index} of ${totalToReview})`
        : getDocumentDisplayName(current.document);
    return <DocumentScanningModal noun={noun} fileLabel={label} onCancel={finishFlow} />;
  }

  if (phase === "review" && current?.result) {
    return (
      <DocumentReviewModal
        document={current.document}
        noun={noun}
        totalLabel={totalLabel}
        getFileUrl={getFileUrl}
        result={current.result}
        busy={busy}
        onConfirm={handleConfirm}
        onClose={advance}
        onRescan={() => startScan(current.document, queue)}
      />
    );
  }

  // ---- stage phase ----
  if (!items.length) {
    return (
      <ModalShell
        title={`Upload ${noun}`}
        width="w-[500px]"
        onClose={onClose}
        footer={<GhostButton onClick={onClose}>Cancel</GhostButton>}
      >
        <Dropzone
          accept={accept}
          multiple={multiple}
          title="Upload Document"
          hint="Drag & Drop files here"
          footer={footer}
          onFiles={addFiles}
          onReject={(message) => toast(message)}
        />
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={`Upload ${noun}`}
      badge={`${items.length} File${items.length > 1 ? "s" : ""}`}
      width="w-[500px]"
      onClose={onClose}
      footer={
        <>
          {multiple && (
            <label className="h-[40px] px-1 flex items-center gap-2 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#0065FD] cursor-pointer mr-auto">
              <IoAdd size={16} />
              Add More
              <input
                type="file"
                accept={accept}
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) addFiles(files);
                  if (e.target) e.target.value = null;
                }}
              />
            </label>
          )}
          <GhostButton onClick={onClose} disabled={uploading}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleUploadAndScan} loading={uploading}>
            Upload And Scan
          </PrimaryButton>
        </>
      }
    >
      <div className="px-4 pt-4 pb-6 flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-4 p-4 rounded-[12px] border border-[#E5E8F3] bg-[#F8F9FE]"
            >
              {item.previewUrl ? (
                <div className="w-[48px] h-[48px] shrink-0 rounded-[8px] overflow-hidden bg-[#DBEAFE]">
                  <img src={item.previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <PdfFileIcon />
              )}
              <div className="flex-1 min-w-0 flex flex-col">
                <span
                  className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate"
                  title={item.file.name}
                >
                  {item.file.name}
                </span>
                <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
                  {formatBytes(item.file.size)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="p-2 text-[#7C829A] cursor-pointer hover:bg-slate-100 rounded-[6px]"
                aria-label="Remove file"
              >
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>

        {allowRename && items.length === 1 && (
          <div className="flex flex-col gap-1">
            <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
              Document Name
            </span>
            <input
              type="text"
              value={documentName}
              onChange={(e) => {
                setDocumentName(e.target.value);
                setDocumentNameTouched(true);
              }}
              className="h-[40px] px-3 rounded-[6px] border border-[#E5E8F3] focus:border-[#0065FD] outline-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
            />
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default DocumentUploadScanModal;
